import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import Button from './Button';
import { EditableTimeSegment } from '../../shared/types.js';
import { toDateInputString } from '../utils.js';

interface InsertLunchModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: { begin: number; end: number }) => void;
    segment: EditableTimeSegment | null;
}

const InfoDisplay: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="flex justify-between">
        <span className="text-gray-500 dark:text-gray-400">{label}:</span>
        <span className="font-semibold">{value}</span>
    </div>
);

const InsertLunchModal: React.FC<InsertLunchModalProps> = ({ isOpen, onClose, onSave, segment }) => {
    const formRef = useRef<HTMLFormElement>(null);
    const [beginTime, setBeginTime] = useState('12:00');
    const [endTime, setEndTime] = useState('13:00');
    useEffect(() => {
        if (!isOpen) {
            setBeginTime('12:00');
            setEndTime('13:00');
        }
    }, [isOpen]);
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                formRef.current?.requestSubmit();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onSave]);
    if (!segment) return null;
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const segmentDate = toDateInputString(new Date(segment.startTime));
        const beginTimestamp = new Date(`${segmentDate}T${beginTime}`).getTime();
        const endTimestamp = new Date(`${segmentDate}T${endTime}`).getTime();
        if (beginTimestamp >= endTimestamp) {
            alert('Lunch begin time must be before end time.');
            return;
        }
        const now = Date.now();
        const upperBound = segment.endTime || now;
        if (beginTimestamp <= segment.startTime || endTimestamp > upperBound) {
            alert("Lunch break must be within the original entry's time range.");
            return;
        }
        onSave({ begin: beginTimestamp, end: endTimestamp });
    };
    const inputClasses = "block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary";
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Insert Lunch">
            <form ref={formRef} onSubmit={handleSubmit} className="text-gray-900 dark:text-white">
                <div className="flex flex-col md:flex-row gap-8">
                    <div className="flex-1 space-y-4">
                        <div className="grid grid-cols-3 items-center">
                            <label htmlFor="beginTime" className="text-right pr-4 text-gray-600 dark:text-gray-300">Begin:</label>
                            <input
                                id="beginTime"
                                type="time"
                                value={beginTime}
                                onChange={e => setBeginTime(e.target.value)}
                                required
                                className={`${inputClasses} col-span-2`}
                            />
                        </div>
                        <div className="grid grid-cols-3 items-center">
                            <label htmlFor="endTime" className="text-right pr-4 text-gray-600 dark:text-gray-300">End:</label>
                            <input
                                id="endTime"
                                type="time"
                                value={endTime}
                                onChange={e => setEndTime(e.target.value)}
                                required
                                className={`${inputClasses} col-span-2`}
                            />
                        </div>
                    </div>
                    <div className="flex-1 border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900/40 rounded-lg p-4 space-y-2">
                        <h3 className="text-lg font-bold border-b border-gray-200 dark:border-gray-600 pb-2 mb-3">Original Entry</h3>
                        <InfoDisplay label="Name" value={segment.employeeName} />
                        <InfoDisplay label="Activity" value={segment.activity} />
                        <InfoDisplay label="Date" value={new Date(segment.startTime).toLocaleDateString()} />
                        <InfoDisplay label="Start" value={segment.ui_startTime} />
                        <InfoDisplay label="Stop" value={segment.endTime ? segment.ui_stopTime : 'In Progress'} />
                    </div>
                </div>
                <div className="flex justify-end gap-4 pt-6 mt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button type="submit">Save</Button>
                </div>
            </form>
        </Modal>
    );
};

export default InsertLunchModal;
